import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementBlock as _createElementBlock, mergeProps as _mergeProps, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, createVNode as _createVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-1ad85510"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "input__wrapper" }
const _hoisted_2 = ["for"]
const _hoisted_3 = ["id", "placeholder", "value", "autocomplete", "disabled"]
const _hoisted_4 = ["id", "disabled", "autocomplete", "placeholder"]
const _hoisted_5 = ["selected"]
const _hoisted_6 = {
  key: -1,
  class: "input__option"
}
const _hoisted_7 = ["selected", "value"]
const _hoisted_8 = ["id", "type", "multiple", "name", "value", "placeholder", "disabled", "autocomplete", "data-required"]

import { v4 as uuidV4 } from 'uuid'
import { PropType, computed, watch, ref } from 'vue'
import BaseIcon from '@/components/BaseIcon.vue'
import { Option } from '@/models/shared.model'


export default /*#__PURE__*/_defineComponent({
  __name: 'BaseInput',
  props: {
  modelValue: {
    type: [String, Number, Boolean, null] as PropType<
      string | number | boolean | null
    >,
    default: undefined,
  },
  name: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    default: '',
  },
  required: {
    type: Boolean,
    default: false,
  },
  autocomplete: {
    type: String,
    default: undefined,
  },
  iconLeft: {
    type: String,
    default: null,
  },
  iconRight: {
    type: String,
    default: null,
  },
  iconHeight: {
    type: Number,
    default: undefined,
  },
  iconWidth: {
    type: Number,
    default: undefined,
  },
  iconSize: {
    type: Number,
    default: 24,
  },
  type: {
    type: String,
    default: 'text',
    validator: (value: string) =>
      [
        'text',
        'tel',
        'email',
        'textarea',
        'tel',
        'email',
        'number',
        'password',
        'search',
        'url',
        'file',
        'select',
        'date',
      ].includes(value),
  },
  options: {
    type: Array as PropType<Option[]>,
    default: () => [],
  },
  placeholderOption: {
    type: String,
    default: undefined,
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  resizable: {
    type: Boolean,
    default: false,
  },
  errors: {
    type: Array as PropType<string[] | undefined>,
    default: () => [],
  },
  // Will fallback to label prop if undefined
  placeholder: {
    type: String,
    default: undefined,
  },
},
  emits: [
  'select',
  'update:modelValue',
  'iconClick',
  'iconLeftClick',
  'iconRightClick',
],
  setup(__props, { emit }) {

const props = __props



const id = uuidV4()

// Number type is converited into text type to allow comma logic.
// LocalValue is responsable for keeping correct modalValue.
const localValue = ref(props.modelValue)

watch(
  () => localValue.value,
  () => {
    localValue.value = handleInput(localValue.value)
  },
)

watch(
  () => props.modelValue,
  () => {
    localValue.value = props.modelValue
  },
)

const handleInput = (value: string | number) => {
  if (props.type === 'select') {
    emit('select', value)
  }
  if (props.type === 'number') {
    // If type is number then modelValue allows only numbers, dashes, commas and periods.
    // Commas are changed to periods.
    if (typeof value === 'string') {
      value = value.replace(',', '.').replace(/[^0-9.-]/g, '')
    }
  }
  emit('update:modelValue', value)
  return value
}



const validateOptionsProp = () => {
  if (
    ['select'].includes(props.type) &&
    (!Array.isArray(props.options) ||
      !props.options.every((option) => option.key && option.label))
  ) {
    throw new Error(
      'Option prop must be {key, label}[], sent prop is: ' +
        JSON.stringify(props.options || ''),
    )
  }
}
validateOptionsProp()

const isValueMatchesAnyOption = computed<boolean>(() => {
  return props.options.some((option) => option.key === props.modelValue)
})

watch(() => [props.type, props.options], validateOptionsProp)

return (_ctx: any,_cache: any) => {
  const _component_RequiredAsterisk = _resolveComponent("RequiredAsterisk")!
  const _component_InputErrorMessages = _resolveComponent("InputErrorMessages")!

  return (_openBlock(), _createElementBlock("section", {
    class: _normalizeClass(['input', `input--${__props.type}`, `input--${__props.name}`])
  }, [
    _createElementVNode("div", _hoisted_1, [
      (__props.required || __props.modelValue || typeof __props.modelValue === 'number')
        ? (_openBlock(), _createElementBlock("label", {
            key: 0,
            for: _unref(id),
            class: "input__label"
          }, [
            _createTextVNode(_toDisplayString(__props.label), 1),
            (__props.required)
              ? (_openBlock(), _createBlock(_component_RequiredAsterisk, { key: 0 }))
              : _createCommentVNode("", true)
          ], 8, _hoisted_2))
        : _createCommentVNode("", true),
      (__props.type === 'textarea')
        ? (_openBlock(), _createElementBlock("textarea", _mergeProps({ key: 1 }, _ctx.$attrs, {
            id: _unref(id),
            class: ["input__field input__field--is-textarea", {
          input__field: true,
          'input__field--has-icon-left': __props.iconLeft,
          'input__field--has-icon-right': __props.iconRight,
          'input__field--is-resizable': __props.resizable,
        }],
            rows: "4",
            placeholder: __props.placeholder ?? __props.label,
            value: __props.modelValue,
            autocomplete: __props.autocomplete,
            disabled: __props.disabled,
            onInput: _cache[0] || (_cache[0] = ($event: any) => (handleInput($event.target.value)))
          }), null, 16, _hoisted_3))
        : (__props.type === 'select')
          ? (_openBlock(), _createElementBlock("select", _mergeProps({
              key: 2,
              id: _unref(id),
              class: "input__field input__field--is-select",
              disabled: __props.disabled,
              autocomplete: __props.autocomplete,
              placeholder: __props.placeholder ?? __props.label
            }, _ctx.$attrs, {
              onChange: _cache[1] || (_cache[1] = 
          (e) => {
            handleInput(e.target.value)
          }
        )
            }), [
              _createElementVNode("option", {
                hidden: "",
                disabled: "",
                selected: !_unref(isValueMatchesAnyOption),
                value: ""
              }, _toDisplayString(__props.label), 9, _hoisted_5),
              (__props.placeholderOption)
                ? (_openBlock(), _createElementBlock("option", _hoisted_6, _toDisplayString(__props.placeholderOption), 1))
                : _createCommentVNode("", true),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.options, (option) => {
                return (_openBlock(), _createElementBlock("option", _mergeProps({
                  key: option.key,
                  selected: option.key === __props.modelValue,
                  value: option.key
                }, option.attrs, { class: "input__option" }), _toDisplayString(option.label), 17, _hoisted_7))
              }), 128))
            ], 16, _hoisted_4))
          : (_openBlock(), _createElementBlock("input", _mergeProps({ key: 3 }, _ctx.$attrs, {
              id: _unref(id),
              class: {
          input__field: true,
          'input__field--has-icon-left': __props.iconLeft,
          'input__field--has-icon-right': __props.iconRight,
        },
              type: __props.type === 'number' ? 'text' : __props.type,
              multiple: __props.multiple,
              name: __props.name,
              value: localValue.value,
              placeholder: __props.placeholder ?? __props.label,
              disabled: __props.disabled,
              autocomplete: __props.autocomplete,
              "data-required": __props.required,
              onInput: _cache[2] || (_cache[2] = (event) => (localValue.value = event.target.value))
            }), null, 16, _hoisted_8)),
      (__props.iconLeft || __props.iconRight)
        ? (_openBlock(), _createBlock(BaseIcon, {
            key: 4,
            class: _normalizeClass(["input__icon", {
          'input__icon--left': __props.iconLeft,
          'input__icon--right': __props.iconRight,
        }]),
            icon: __props.iconLeft || __props.iconRight,
            height: __props.iconHeight,
            width: __props.iconWidth,
            size: __props.iconSize,
            onClick: _cache[3] || (_cache[3] = 
          () => {
            _ctx.$emit('iconClick', _ctx.$event)
            if (__props.iconLeft) {
              _ctx.$emit('iconLeftClick', _ctx.$event)
            }
            if (__props.iconRight) {
              _ctx.$emit('iconRightClick', _ctx.$event)
            }
          }
        )
          }, null, 8, ["class", "icon", "height", "width", "size"]))
        : _createCommentVNode("", true)
    ]),
    _createVNode(_component_InputErrorMessages, { "model-value": __props.errors }, null, 8, ["model-value"])
  ], 2))
}
}

})